/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMutation, useQuery, useQueryClient, UseQueryResult } from 'react-query';
import { api } from '../constants';
import { AxiosInstance } from '../axios.config';
import { IProduct } from '../manageProduct/types';
import { SizeGuideResponse, StyleField } from './types';
import { AddOnFields, AddOnFieldsResponse } from './types';

//get
const getCategories = async () => {
  const response = await AxiosInstance.get(api.product.categories);
  return response.data;
};

const useGetCategories = () => {
  return useQuery('categories', getCategories);
};

const getSubCategories = async (id: number) => {
  // api/v1/catalog/vendor/categories/id/sub-categories/
  //adding id to the subCategories constant
  const response = await AxiosInstance.get(api.product.subCategories.replace('id', id.toString()));
  return response.data;
};

const useGetSubCategories = (id: number) => {
  return useQuery({
    queryKey: ['subCategories', id],
    queryFn: () => getSubCategories(id),
    enabled: !!id,
  });
};

const getBrands = async () => {
  const response = await AxiosInstance.get(api.product.brands);
  return response.data;
};

const useGetBrands = () => {
  return useQuery('brands', getBrands);
};
// In your services/addProduct.ts file
const getStyleFields = async (): Promise<StyleField[]> => {
  const response = await AxiosInstance.get(api.product.styleFields);
  return response.data; // Directly return the array
};

export const useGetStyleFields = () => {
  return useQuery<StyleField[]>({
    queryKey: ['styleFields'],
    queryFn: getStyleFields,
    staleTime: 1000 * 60 * 5, // 5 minutes
    cacheTime: 1000 * 60 * 10, // 10 minutes
    refetchOnWindowFocus: true, // Good for data freshness
  });
};
// Function to fetch add-on fields
const getAddOnFields = async (): Promise<AddOnFields[]> => {
  try {
    const response = await AxiosInstance.get<AddOnFieldsResponse>(api.product.addOnFields);
    return Array.isArray(response.data) ? response.data : [];
  } catch {
    return [];
  }
};

// Hook to use the add-on fields
export const useGetAddOnFields = () => {
  return useQuery<AddOnFields[], Error>({
    queryKey: ['addOnFields'], // Updated key to reflect add-ons
    queryFn: getAddOnFields, // Corrected to use getAddOnFields
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    cacheTime: 1000 * 60 * 10, // Keep data in cache for 10 minutes
  });
};

const getColors = async () => {
  const response = await AxiosInstance.get(api.product.colors);
  return response.data;
};

const useGetColors = (enabled: boolean = true) => {
  return useQuery({
    queryKey: ['colors'],
    queryFn: getColors,
    enabled, // Only fetch when enabled is true
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    cacheTime: 1000 * 60 * 10, // Keep data in cache for 10 minutes
  });
};

const getSizes = async () => {
  const response = await AxiosInstance.get(api.product.sizes);
  return response.data;
};

const useGetSizes = (enabled: boolean = true) => {
  return useQuery({
    queryKey: ['sizes'],
    queryFn: getSizes,
    enabled, // Only fetch when enabled is true
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    cacheTime: 1000 * 60 * 10, // Keep data in cache for 10 minutes
  });
};

const getCollections = async () => {
  const response = await AxiosInstance.get(api.product.collection);
  return response.data;
};

const useGetCollections = () => {
  return useQuery('collection', getCollections);
};

const getFabrics = async () => {
  const response = await AxiosInstance.get(api.product.fabrics);
  return response.data;
};
const useGetFabrics = () => {
  return useQuery('fabrics', getFabrics);
};

export interface IMeasurementField {
  id: number;
  name: string;
  is_active: boolean;
  created_at: string;
}

const getMeasurementFields = async (): Promise<IMeasurementField[]> => {
  const response = await AxiosInstance.get(api.product.measurementFields);
  return response.data;
};

const useGetMeasurementFields = () => {
  return useQuery('measurementFields', getMeasurementFields);
};

// post
const postProduct = async (product: FormData) => {
  const response = await AxiosInstance.post(api.product.addProduct, product, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

const usePostProduct = () => {
  return useMutation({
    mutationFn: (product: FormData) => postProduct(product),
  });
};
const editProduct = async (product: FormData, id: string) => {
  const response = await AxiosInstance.patch(api.product.editProduct + id + '/', product, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

const useEditProduct = (id: string) => {
  return useMutation({
    mutationFn: (product: FormData) => editProduct(product, id),
  });
};

// remove variant
const removeVariant = async (variant_ids: number[]) => {
  const response = await AxiosInstance.delete(api.product.removeVariant, {
    data: { variant_ids },
  });
  return response.data;
};

const useRemoveVariant = () => {
  return useMutation({
    mutationFn: (variant_ids: number[]) => removeVariant(variant_ids),
  });
};

// remove variant size
const removeVariantSize = async (variantIds: number[]) => {
  const response = await AxiosInstance.delete(api.product.removeVariantSize, {
    data: { variant_ids: variantIds }, // Changed key to variant_ids
  });
  return response.data;
};

const useRemoveVariantSize = () => {
  return useMutation({
    mutationFn: (variantIds: number[]) => removeVariantSize(variantIds),
  });
};

const getProductById = async (id: string): Promise<IProduct> => {
  const response = await AxiosInstance.get(api.manageProduct.product_detail.replace(':id', id));
  return response.data;
};

const useGetProductDetail = (id: string | undefined): UseQueryResult<IProduct> => {
  return useQuery({
    queryKey: ['product_detail', id],
    queryFn: () => getProductById(id!),
    enabled: !!id && id !== '0' && id !== '',

    staleTime: 1000 * 60 * 5,
    cacheTime: 1000 * 60 * 10,
  });
};

const removeImage = async (imageId: number | undefined) => {
  const response = await AxiosInstance.delete(`${api.product.removeVariantImage}${imageId}/`);
  return response.data;
};

const useRemoveImage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (imageId: number) => removeImage(imageId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product_detail'] });
    },
  });
};

// Add this to your services/addProduct.ts file
const getCurrencyByLocation = async (): Promise<{ currency: string }> => {
  const response = await AxiosInstance.get(api.product.getCurrency);
  return response.data;
};

const useGetCurrency = () => {
  return useQuery({
    queryKey: ['currency'],
    queryFn: getCurrencyByLocation,
    staleTime: Infinity, // Currency rarely changes for a user
    cacheTime: Infinity,
  });
};

const getSizeGuides = async (
  categoryId?: number | string,
  page: number = 1,
): Promise<SizeGuideResponse> => {
  const params: Record<string, any> = { page, page_size: 10 };
  if (categoryId) params.category = categoryId;

  const response = await AxiosInstance.get(api.product.sizeGuide, { params });
  return response.data;
};

const useGetSizeGuides = (
  categoryId?: number | string,
  page: number = 1,
  options?: { enabled?: boolean },
) => {
  return useQuery({
    queryKey: ['sizeGuides', categoryId, page],
    queryFn: () => getSizeGuides(categoryId, page),
    enabled: options?.enabled ?? true, // ← Critical
    keepPreviousData: true,
    staleTime: 1000 * 60 * 5,
  });
};

const getAddOnFieldsByCategory = async (categoryId: string | number): Promise<AddOnFields[]> => {
  try {
    const response = await AxiosInstance.get(
      api.product.addOnFieldsByCategory.replace(':category_id', String(categoryId)),
    );
    return Array.isArray(response.data.results) ? response.data.results : [];
  } catch {
    return [];
  }
};

// Add this NEW hook
const useGetAddOnFieldsByCategory = (categoryId?: string | number) => {
  return useQuery<AddOnFields[], Error>({
    queryKey: ['addOnFieldsByCategory', categoryId],
    queryFn: () => getAddOnFieldsByCategory(categoryId!),
    enabled: !!categoryId,
    staleTime: 1000 * 60 * 5,
    cacheTime: 1000 * 60 * 10,
  });
};

export {
  useGetBrands,
  useGetColors,
  useGetCategories,
  useGetFabrics,
  useGetSizes,
  useGetSubCategories,
  useGetCollections,
  usePostProduct,
  useEditProduct,
  useRemoveVariant,
  useRemoveVariantSize,
  useRemoveImage,
  useGetProductDetail,
  useGetMeasurementFields,
  useGetCurrency,
  useGetSizeGuides,
  useGetAddOnFieldsByCategory,
};
